import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { FaChevronDown, FaMapMarkerAlt } from "react-icons/fa";
import {
  ACTIVITY_API_BASE,
  getCountryImageUrl,
  getCityImageUrl,
  UNSPLASH_HERO,
} from "../lib/activityApi";

const BASE_URL = ACTIVITY_API_BASE.replace("/api/activity", "");

export default function ActivitiesPage() {
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countryFilter, setCountryFilter] = useState(""); // dropdown value: "" = all, or countryId
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingCities, setLoadingCities] = useState(false);
  const [heroSlide, setHeroSlide] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setLoadingCountries(true);
    axios
      .get(`${ACTIVITY_API_BASE}/user/countries`)
      .then((res) => setCountries(res.data?.data || []))
      .catch(() => setCountries([]))
      .finally(() => setLoadingCountries(false));
  }, []);

  useEffect(() => {
    if (!selectedCountry?._id) {
      setCities([]);
      return;
    }
    setLoadingCities(true);
    axios
      .get(`${ACTIVITY_API_BASE}/user/cities/${selectedCountry._id}`)
      .then((res) => setCities(res.data?.data || []))
      .catch(() => setCities([]))
      .finally(() => setLoadingCities(false));
  }, [selectedCountry?._id]);

  useEffect(() => {
    const t = setInterval(
      () => setHeroSlide((i) => (i + 1) % (UNSPLASH_HERO?.length || 1)),
      5000
    );
    return () => clearInterval(t);
  }, []);

  const filteredCountries = countryFilter
    ? countries.filter((c) => c._id === countryFilter)
    : countries;

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setCountryFilter(country?._id || "");
  };

  const handleCityClick = (city) => {
    if (selectedCountry?._id && city?._id)
      navigate(`/activities/${selectedCountry._id}/${city._id}`);
  };

  const heroBg =
    UNSPLASH_HERO && UNSPLASH_HERO[heroSlide]
      ? UNSPLASH_HERO[heroSlide]
      : "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1600&q=80";

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      {/* Hero */}
      <section className="relative h-[320px] md:h-[380px] w-full overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-700"
          style={{ backgroundImage: `url('${heroBg}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50" />
        <div className="relative z-10 h-full flex flex-col justify-center items-center text-center px-4">
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white drop-shadow-lg mb-2"
          >
            Discover Activities
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-white/95 text-lg md:text-xl max-w-2xl"
          >
            Choose a country, pick a city, and book experiences.
          </motion.p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
        {/* Country filter */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-[#222222]">
            {selectedCountry
              ? `Cities in ${selectedCountry.name}`
              : "Explore by country"}
          </h2>
          <div className="relative w-full sm:w-56">
            <select
              value={countryFilter}
              onChange={(e) => {
                const id = e.target.value;
                setCountryFilter(id);
                const c = countries.find((x) => x._id === id) || null;
                setSelectedCountry(c);
              }}
              className="w-full appearance-none pl-4 pr-10 py-3 rounded-xl border border-gray-200 bg-white text-gray-700 font-medium focus:outline-none focus:ring-2 focus:ring-[#1ABCE3] focus:border-transparent"
            >
              <option value="">All countries</option>
              {countries.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name}
                </option>
              ))}
            </select>
            <FaChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Country cards grid */}
        {!selectedCountry && (
          <>
            {loadingCountries ? (
              <div className="flex justify-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1ABCE3]" />
              </div>
            ) : filteredCountries.length === 0 ? (
              <p className="text-center text-gray-500 py-12">
                No countries available.
              </p>
            ) : (
              <motion.div
                layout
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6"
              >
                {filteredCountries.map((country, idx) => (
                  <motion.button
                    key={country._id}
                    type="button"
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.04 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleCountrySelect(country)}
                    className="rounded-2xl overflow-hidden shadow-md bg-white hover:shadow-xl transition-shadow text-left border border-gray-100"
                  >
                    <div className="relative h-44 md:h-52 overflow-hidden">
                      <img
                        src={getCountryImageUrl(country.image, BASE_URL)}
                        alt={country.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2 text-white">
                        <FaMapMarkerAlt className="w-4 h-4 shrink-0" />
                        <span className="font-bold text-lg drop-shadow-lg">
                          {country.name}
                        </span>
                      </div>
                    </div>
                    <div className="p-4 flex items-center justify-between">
                      <span className="text-sm text-gray-600">
                        View cities & activities
                      </span>
                      <span className="text-sm font-semibold text-[#1ABCE3]">
                        Explore →
                      </span>
                    </div>
                  </motion.button>
                ))}
              </motion.div>
            )}
          </>
        )}

        {/* Selected country: show cities */}
        <AnimatePresence mode="wait">
          {selectedCountry && (
            <motion.div
              key={selectedCountry._id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-6"
            >
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCountry(null);
                    setCountryFilter("");
                  }}
                  className="text-sm font-medium text-[#1ABCE3] hover:underline"
                >
                  ← Back to all countries
                </button>
              </div>

              {loadingCities ? (
                <div className="flex justify-center py-16">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1ABCE3]" />
                </div>
              ) : cities.length === 0 ? (
                <p className="text-center text-gray-500 py-12">
                  No cities in this country yet.
                </p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {cities.map((city, idx) => (
                    <motion.button
                      key={city._id}
                      type="button"
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.03 }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleCityClick(city)}
                      className="rounded-xl overflow-hidden shadow-md border border-gray-100 bg-white hover:shadow-lg transition-all text-left"
                    >
                      <div className="relative h-32 overflow-hidden">
                        <img
                          src={getCityImageUrl(city.image, BASE_URL)}
                          alt={city.name}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <span className="absolute bottom-2 left-2 right-2 text-white font-bold text-sm drop-shadow-lg">
                          {city.name}
                        </span>
                      </div>
                      <div className="p-3">
                        <span className="text-xs text-gray-500">
                          Activities in {city.name}
                        </span>
                      </div>
                    </motion.button>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
